import { AlertTriangle, Check, Download, Pencil, Trash2, X } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { FileUploadZone } from '../../../components/FileUploadZone';
import {
  Box,
  Divider,
  FormInput,
  HStack,
  IconButton,
  Text,
  VStack,
} from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { PickedFile } from '../../../utils/fileFormData';
import type {
  OrganizationProduct,
  ProductImportResult,
  ProductImportWarningRow,
  ProductImportWarningType,
  ProductKind,
} from '../types';
import { importRejectionParams, importWarningParams } from '../utils/importMessageParams';
import { resolveLiveImportWarnings } from '../utils/importWarnings';
import { productErrorMessageParams } from '../utils/productErrors';
import {
  MAX_PRODUCT_DESCRIPTION_LENGTH,
  MAX_PRODUCT_NAME_LENGTH,
  MAX_PRODUCT_PRICE_RANGE_LENGTH,
} from '../utils/productLimits';
import {
  buildCreateProductExtras,
  buildProductUpdateChanges,
  EMPTY_PRODUCT_FIELDS,
  type ProductFormFields,
  type ProductTextFields,
  productToFormFields,
} from '../utils/productPayload';
import { DeleteProductDialog } from './DeleteProductDialog';
import { ProductAliasesInput } from './ProductAliasesInput';
import { ProductKindAliasesSummary } from './ProductKindAliasesSummary';
import { ProductKindSelector } from './ProductKindSelector';
import { ProductNameGuidance } from './ProductNameGuidance';

const IMPORT_WARNING_KEYS: Record<
  ProductImportWarningType,
  { plain: string; withSuggestion: string }
> = {
  generic_name: {
    plain: 'onboardingWizard.products.import.warnings.generic_name',
    withSuggestion: 'onboardingWizard.products.import.warnings.generic_name_with_suggestion',
  },
  aliases_truncated: {
    plain: 'onboardingWizard.products.import.warnings.aliases_truncated',
    withSuggestion: 'onboardingWizard.products.import.warnings.aliases_truncated',
  },
  unknown_kind: {
    plain: 'onboardingWizard.products.import.warnings.unknown_kind',
    withSuggestion: 'onboardingWizard.products.import.warnings.unknown_kind',
  },
};

const UNKNOWN_IMPORT_WARNING_KEY = 'onboardingWizard.products.import.warnings.unknown';

const importWarningKey = (warning: ProductImportWarningRow): string => {
  const keys = IMPORT_WARNING_KEYS[warning.warning];
  if (!keys) return UNKNOWN_IMPORT_WARNING_KEY;
  return warning.suggestedName ? keys.withSuggestion : keys.plain;
};

export interface ProductDraft {
  name: string;
  description?: string;
  priceRange?: string;
  kind?: ProductKind;
  aliases?: string[];
}

export interface ProductEdit {
  name: string;
  description: string;
  priceRange: string;
  kind?: ProductKind;
  aliases?: string[];
}

interface ProductCatalogStepProps {
  organizationId: string;
  errorMessageKey?: string | null;
  products: OrganizationProduct[];
  onAdd: (draft: ProductDraft) => void;
  onRemove: (productId: string) => void;
  onUpdate: (productId: string, edit: ProductEdit) => void;
  onNext: () => void;
  isAdding: boolean;
  isUpdating: boolean;
  onImportFile: (file: PickedFile) => void;
  isImporting: boolean;
  importResult: ProductImportResult | null;
  importError: boolean;
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
}

export function ProductCatalogStep({
  products,
  onAdd,
  onRemove,
  onUpdate,
  onNext,
  isAdding,
  isUpdating,
  onImportFile,
  isImporting,
  importResult,
  importError,
  onDownloadTemplate,
  isDownloadingTemplate,
  organizationId,
  errorMessageKey = null,
}: Readonly<ProductCatalogStepProps>) {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<OrganizationProduct | null>(null);
  const [draft, setDraft] = useState<ProductFormFields>(EMPTY_PRODUCT_FIELDS);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ProductFormFields>(EMPTY_PRODUCT_FIELDS);

  const liveWarnings = useMemo(
    () => (importResult ? resolveLiveImportWarnings(importResult, products) : []),
    [importResult, products],
  );

  const applyImportSuggestion = (productId: string, suggestion: string) => {
    const product = products.find((candidate) => candidate.id === productId);
    onUpdate(productId, {
      name: suggestion,
      description: '',
      priceRange: product?.priceRange ?? '',
    });
  };

  const trimmedEditName = editDraft.name.trim();
  const canSave = trimmedEditName.length > 0 && !isUpdating;

  const applyEditSuggestion = (suggestion: string) => {
    setEditDraft((prev) => ({ ...prev, name: suggestion, description: '' }));
  };

  const applyDraftSuggestion = (suggestion: string) => {
    setDraft((prev) => ({ ...prev, name: suggestion, description: '' }));
  };

  const setEditField = (field: keyof ProductTextFields) => (text: string) => {
    setEditDraft((prev) => ({ ...prev, [field]: text }));
  };

  const startEdit = (product: OrganizationProduct) => {
    setEditingId(product.id);
    setEditDraft(productToFormFields(product));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY_PRODUCT_FIELDS);
  };

  const handleSave = () => {
    if (!editingId || !canSave) return;

    const loaded = products.find((product) => product.id === editingId);

    onUpdate(editingId, {
      name: trimmedEditName,
      description: editDraft.description.trim(),
      priceRange: editDraft.priceRange.trim(),
      ...(loaded ? buildProductUpdateChanges(loaded, editDraft) : {}),
    });

    cancelEdit();
  };

  const trimmedName = draft.name.trim();
  const canAdd = trimmedName.length > 0 && !isAdding;

  const setField = (field: keyof ProductTextFields) => (text: string) => {
    setDraft((prev) => ({ ...prev, [field]: text }));
  };

  const handleAdd = () => {
    if (!canAdd) return;

    onAdd({ name: trimmedName, ...buildCreateProductExtras(draft) });

    setDraft(EMPTY_PRODUCT_FIELDS);
  };

  return (
    <VStack spacing="md" testID="onboarding-products-step">
      <Text variant="h4" color={colors.text.primary}>
        {t('onboardingWizard.products.title')}
      </Text>
      <Text variant="body" color={colors.text.secondary}>
        {t('onboardingWizard.products.subtitle')}
      </Text>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.products.nameLabel')}
        </Text>
        <FormInput
          testID="onboarding-products-name"
          maxLength={MAX_PRODUCT_NAME_LENGTH}
          value={draft.name}
          onChangeText={setField('name')}
          placeholder={t('onboardingWizard.products.namePlaceholder')}
          accessibilityLabel={t('onboardingWizard.products.nameLabel')}
          style={styles.input}
        />
        <ProductNameGuidance
          testIDPrefix="onboarding-products-guidance"
          name={draft.name}
          description={draft.description}
          priceRange={draft.priceRange}
          aliases={draft.aliases}
          onUseSuggestion={applyDraftSuggestion}
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.products.descriptionLabel')}
        </Text>
        <FormInput
          testID="onboarding-products-description"
          maxLength={MAX_PRODUCT_DESCRIPTION_LENGTH}
          value={draft.description}
          onChangeText={setField('description')}
          placeholder={t('onboardingWizard.products.descriptionPlaceholder')}
          accessibilityLabel={t('onboardingWizard.products.descriptionLabel')}
          style={styles.input}
          multiline
        />
      </VStack>

      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('onboardingWizard.products.priceRangeLabel')}
        </Text>
        <FormInput
          testID="onboarding-products-priceRange"
          maxLength={MAX_PRODUCT_PRICE_RANGE_LENGTH}
          value={draft.priceRange}
          onChangeText={setField('priceRange')}
          placeholder={t('onboardingWizard.products.priceRangePlaceholder')}
          accessibilityLabel={t('onboardingWizard.products.priceRangeLabel')}
          style={styles.input}
        />
      </VStack>

      <ProductKindSelector
        testIDPrefix="onboarding-products-kind"
        value={draft.kind}
        onChange={(kind) => setDraft((prev) => ({ ...prev, kind }))}
      />

      <ProductAliasesInput
        testIDPrefix="onboarding-products-aliases"
        aliases={draft.aliases}
        productName={draft.name}
        onChange={(aliases) => setDraft((prev) => ({ ...prev, aliases }))}
      />

      <Box testID="onboarding-products-add-row" alignItems="flex-end" marginTop="sm">
        <Button
          testID="onboarding-products-add"
          variant="outline"
          size="sm"
          onPress={handleAdd}
          disabled={!canAdd}
          loading={isAdding}
          accessibilityState={{ disabled: !canAdd }}
        >
          {t('onboardingWizard.products.add')}
        </Button>
      </Box>

      <VStack spacing="sm" testID="onboarding-products-import">
        <HStack spacing="sm" align="center">
          <Divider style={styles.dividerLine} />
          <Text variant="caption" color={colors.text.secondary}>
            {t('onboardingWizard.products.import.divider')}
          </Text>
          <Divider style={styles.dividerLine} />
        </HStack>

        <Text variant="caption" color={colors.text.secondary}>
          {t('onboardingWizard.products.import.hint')}
        </Text>

        <FileUploadZone
          onFileSelected={onImportFile}
          selectedFile={null}
          onClear={() => undefined}
          disabled={isImporting}
        />

        <Button
          testID="onboarding-products-download-template"
          variant="ghost"
          size="sm"
          onPress={onDownloadTemplate}
          loading={isDownloadingTemplate}
          accessibilityLabel={t('onboardingWizard.products.import.downloadTemplate')}
        >
          <HStack spacing="xs" align="center">
            <Download size={14} color={colors.primary[500]} />
            <Text variant="caption" color={colors.primary[500]}>
              {t('onboardingWizard.products.import.downloadTemplate')}
            </Text>
          </HStack>
        </Button>

        {importError ? (
          <Text
            testID="onboarding-products-import-error"
            variant="caption"
            color={colors.status.error.main}
            accessibilityRole="alert"
          >
            {t('onboardingWizard.products.import.error')}
          </Text>
        ) : null}

        {importResult ? (
          <Box
            testID="onboarding-products-import-summary"
            style={styles.summary}
            accessibilityLiveRegion="polite"
          >
            <Text variant="body" color={colors.text.primary}>
              {t('onboardingWizard.products.import.summary', {
                added: importResult.created.length,
                skipped: importResult.rejectedRows.length,
              })}
            </Text>
            {importResult.rejectedRows.length > 0 ? (
              <VStack spacing="xs" testID="onboarding-products-import-rejected-list">
                {importResult.rejectedRows.map((rejected) => (
                  <HStack
                    key={rejected.row}
                    spacing="xs"
                    align="center"
                    testID={`onboarding-products-import-rejected-${rejected.row}`}
                  >
                    <AlertTriangle size={12} color={colors.status.warning.main} />
                    <Text variant="caption" color={colors.text.primary} style={styles.label}>
                      {t('onboardingWizard.products.import.rowLabel', { row: rejected.row })}
                    </Text>
                    <Text variant="caption" color={colors.text.secondary}>
                      {t(
                        `onboardingWizard.products.import.reasons.${rejected.reason}`,
                        importRejectionParams(rejected.reason),
                      )}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            ) : null}
            {liveWarnings.length > 0 ? (
              <VStack spacing="xs" testID="onboarding-products-import-warnings-list">
                <Text variant="caption" color={colors.text.primary} style={styles.label}>
                  {t('onboardingWizard.products.import.warningsTitle')}
                </Text>
                {liveWarnings.map(({ warning, productId }) => {
                  const warningTestID = `onboarding-products-import-warning-${warning.row}-${warning.warning}`;
                  return (
                    <HStack
                      key={`${warning.row}-${warning.warning}`}
                      spacing="xs"
                      align="center"
                      testID={warningTestID}
                    >
                      <AlertTriangle size={12} color={colors.status.warning.main} />
                      <Text variant="caption" color={colors.text.primary} style={styles.label}>
                        {t('onboardingWizard.products.import.rowLabel', { row: warning.row })}
                      </Text>
                      <Text variant="caption" color={colors.text.secondary}>
                        {t(importWarningKey(warning), {
                          name: warning.name,
                          suggestion: warning.suggestedName ?? '',
                          ...importWarningParams(warning.warning),
                        })}
                      </Text>
                      {warning.warning === 'generic_name' && warning.suggestedName ? (
                        <Button
                          testID={`${warningTestID}-use-suggestion`}
                          variant="outline"
                          size="sm"
                          onPress={() =>
                            applyImportSuggestion(productId, warning.suggestedName as string)
                          }
                          accessibilityLabel={t('onboardingWizard.products.naming.useAsNameLabel', {
                            suggestion: warning.suggestedName,
                          })}
                        >
                          {t('onboardingWizard.products.naming.useAsName')}
                        </Button>
                      ) : null}
                    </HStack>
                  );
                })}
              </VStack>
            ) : null}
          </Box>
        ) : null}
      </VStack>

      {products.length === 0 ? (
        <Text testID="onboarding-products-empty" variant="caption" color={colors.text.secondary}>
          {t('onboardingWizard.products.empty')}
        </Text>
      ) : (
        <VStack spacing="sm" testID="onboarding-products-list">
          {products.map((product) => (
            <Box
              key={product.id}
              testID={`onboarding-products-item-${product.id}`}
              style={styles.row}
            >
              {editingId === product.id ? (
                <VStack spacing="sm">
                  <FormInput
                    testID="onboarding-products-edit-name"
                    maxLength={MAX_PRODUCT_NAME_LENGTH}
                    value={editDraft.name}
                    onChangeText={setEditField('name')}
                    placeholder={t('onboardingWizard.products.namePlaceholder')}
                    accessibilityLabel={t('onboardingWizard.products.nameLabel')}
                    style={styles.input}
                  />
                  <ProductNameGuidance
                    testIDPrefix="onboarding-products-edit-guidance"
                    name={editDraft.name}
                    description={editDraft.description}
                    priceRange={editDraft.priceRange}
                    aliases={editDraft.aliases}
                    onUseSuggestion={applyEditSuggestion}
                  />
                  <FormInput
                    testID="onboarding-products-edit-description"
                    maxLength={MAX_PRODUCT_DESCRIPTION_LENGTH}
                    value={editDraft.description}
                    onChangeText={setEditField('description')}
                    placeholder={t('onboardingWizard.products.descriptionPlaceholder')}
                    accessibilityLabel={t('onboardingWizard.products.descriptionLabel')}
                    style={styles.input}
                    multiline
                  />
                  <FormInput
                    testID="onboarding-products-edit-priceRange"
                    maxLength={MAX_PRODUCT_PRICE_RANGE_LENGTH}
                    value={editDraft.priceRange}
                    onChangeText={setEditField('priceRange')}
                    placeholder={t('onboardingWizard.products.priceRangePlaceholder')}
                    accessibilityLabel={t('onboardingWizard.products.priceRangeLabel')}
                    style={styles.input}
                  />
                  <ProductKindSelector
                    testIDPrefix="onboarding-products-edit-kind"
                    value={editDraft.kind}
                    onChange={(kind) => setEditDraft((prev) => ({ ...prev, kind }))}
                  />
                  <ProductAliasesInput
                    testIDPrefix="onboarding-products-edit-aliases"
                    aliases={editDraft.aliases}
                    productName={editDraft.name}
                    onChange={(aliases) => setEditDraft((prev) => ({ ...prev, aliases }))}
                  />
                  <HStack spacing="sm" justify="end">
                    <IconButton
                      testID="onboarding-products-edit-cancel"
                      icon={<X size={18} color={colors.text.secondary} />}
                      variant="ghost"
                      size="lg"
                      onPress={cancelEdit}
                      accessibilityLabel={t('onboardingWizard.products.cancel')}
                    />
                    <IconButton
                      testID="onboarding-products-edit-save"
                      icon={<Check size={18} color={colors.text.primary} />}
                      variant="filled"
                      size="lg"
                      onPress={handleSave}
                      disabled={!canSave}
                      accessibilityLabel={t('onboardingWizard.products.save')}
                    />
                  </HStack>
                </VStack>
              ) : (
                <HStack spacing="sm" align="center" justify="between">
                  <VStack spacing="xs" fill>
                    <Text variant="body" color={colors.text.primary}>
                      {product.name}
                    </Text>
                    {product.description ? (
                      <Text variant="caption" color={colors.text.secondary}>
                        {product.description}
                      </Text>
                    ) : null}
                    {product.priceRange ? (
                      <Text variant="caption" color={colors.text.secondary}>
                        {product.priceRange}
                      </Text>
                    ) : null}
                    <ProductKindAliasesSummary
                      testID={`onboarding-products-meta-${product.id}`}
                      product={product}
                    />
                  </VStack>
                  <HStack spacing="xs" align="center">
                    <IconButton
                      testID={`onboarding-products-edit-${product.id}`}
                      icon={<Pencil size={18} color={colors.text.secondary} />}
                      variant="ghost"
                      size="lg"
                      onPress={() => startEdit(product)}
                      accessibilityLabel={`${t('onboardingWizard.products.edit')}: ${product.name}`}
                    />
                    <IconButton
                      testID={`onboarding-products-remove-${product.id}`}
                      icon={<Trash2 size={18} color={colors.text.secondary} />}
                      variant="ghost"
                      size="lg"
                      onPress={() => setPendingDelete(product)}
                      accessibilityLabel={`${t('onboardingWizard.products.remove')}: ${product.name}`}
                    />
                  </HStack>
                </HStack>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {errorMessageKey ? (
        <Text
          testID="onboarding-products-error"
          variant="caption"
          color={colors.status.error.main}
          accessibilityRole="alert"
        >
          {t(errorMessageKey, productErrorMessageParams(errorMessageKey))}
        </Text>
      ) : null}

      <DeleteProductDialog
        organizationId={organizationId}
        product={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={(productId) => {
          setPendingDelete(null);
          onRemove(productId);
        }}
      />

      <Button testID="onboarding-products-next" variant="primary" onPress={onNext}>
        {t('onboardingWizard.products.next')}
      </Button>
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
  dividerLine: {
    flex: 1,
  },
  summary: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    gap: tokens.spacing.sm,
  },
  row: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
});
